import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../../src/components/ui/card';
import { Badge } from '../../../../src/components/ui/badge';
import { Button } from '../../../../src/components/ui/button';
import {
  Users,
  Crown,
  Shield,
  DollarSign,
  MessageCircle,
  ChevronDown,
  ChevronRight,
  Building2,
  Info,
  FileText,
  Gavel,
  Vote,
  Truck,
  AlertTriangle,
  Scale,
  FolderOpen,
} from 'lucide-react';

const departments = [
  {
    name: 'Community Property',
    icon: Building2,
    color: 'emerald',
    description: 'Community facilities and property management',
    directorCount: 2,
    nestedDepartments: [],
  },
  {
    name: 'Compliance',
    icon: Shield,
    color: 'blue',
    description: 'Governance compliance oversight',
    directorCount: 1,
    nestedDepartments: [],
  },
  {
    name: 'Election',
    icon: Vote,
    color: 'purple',
    description: 'Board elections and voting processes',
    directorCount: 2,
    nestedDepartments: [],
  },
  {
    name: 'Emergency Road Management',
    icon: AlertTriangle,
    color: 'red',
    description: 'Emergency road response and coordination',
    directorCount: 1,
    nestedDepartments: [],
  },
  {
    name: 'Finance',
    icon: DollarSign,
    color: 'green',
    description: 'Budget oversight and financial planning',
    directorCount: 2,
    nestedDepartments: [],
  },
  {
    name: 'Governing Documents',
    icon: FileText,
    color: 'indigo',
    description: 'Document management and governance rules',
    directorCount: 2,
    nestedDepartments: [],
  },
  {
    name: 'Legal',
    icon: Scale,
    color: 'slate',
    description: 'Legal matters and board oversight',
    directorCount: 2,
    nestedDepartments: [],
  },
  {
    name: 'Project Review',
    icon: FolderOpen,
    color: 'orange',
    description: 'Major project evaluation and approval',
    directorCount: 3,
    nestedDepartments: [],
  },
  {
    name: 'Roads',
    icon: Truck,
    color: 'teal',
    description: 'Road maintenance and snowplow dispatch',
    directorCount: 2,
    nestedDepartments: [],
  },
] as const;

export function HierarchicalGovernanceChart() {
  const [expandedDepartments, setExpandedDepartments] = useState<string[]>([]);

  const toggleDepartment = (deptName: string) => {
    setExpandedDepartments((prev) =>
      prev.includes(deptName) ? prev.filter((d) => d !== deptName) : [...prev, deptName],
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="w-5 h-5" />
          Your Leadership Structure, Organized
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          How your existing roles fit into a clear chain of command
        </p>
      </CardHeader>
      <div className="p-4 rounded-lg bg-gray-50 border border-gray-200 flex items-start gap-3 mx-6">
        <Info className="w-5 h-5 text-gray-500 mt-1 shrink-0" />
        <p className="text-sm text-gray-600">
          Based on real Montana community governance structures. This framework organizes your
          existing committees and responsibilities into a clear chain of command, so road
          maintenance, budget decisions, and legal compliance don't stall. Unlike traditional boards
          that rely on handshakes and emails, this structure is built into the software so every
          decision is recorded, every responsibility is tracked, and nothing falls through the
          cracks.
        </p>
      </div>
      <CardContent className="overflow-x-auto">
        <div className="space-y-8 py-4 min-w-0">
          {/* Community Level */}
          <div className="flex flex-col items-center space-y-6">
            {/* Community Elect */}
            <div className="flex flex-col items-center space-y-4">
              <div className="w-40 sm:w-48 h-20 sm:h-24 bg-linear-to-br from-yellow-300 to-yellow-500 border-2 border-yellow-600 rounded-xl flex flex-col items-center justify-center shadow-lg">
                <Crown className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-900 mb-1" />
                <div className="text-base sm:text-lg font-bold text-yellow-900 whitespace-nowrap">
                  Community Elect
                </div>
                <div className="text-xs text-yellow-800 whitespace-nowrap">Override Authority</div>
              </div>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 max-w-xs text-center">
                <div className="text-xs font-semibold text-gray-700 mb-1">Legacy Role:</div>
                <div className="text-sm text-gray-800 font-medium">President/Chair</div>
                <div className="text-xs italic text-gray-600">"I make final calls"</div>
              </div>
            </div>

            {/* Connection line down */}
            <div className="w-px h-8 bg-gray-400"></div>

            {/* Community Directors */}
            <div className="space-y-4">
              <div className="text-center">
                <div className="text-sm font-semibold text-gray-700 mb-2">Community Directors</div>
                {/* Connection lines - responsive width */}
                <div className="w-64 xs:w-80 sm:w-96 h-px bg-gray-400 mx-auto"></div>
                <div className="relative">
                  <div className="absolute -top-px left-1/3 w-px h-6 sm:h-8 bg-gray-400"></div>
                  <div className="absolute -top-px left-1/2 w-px h-6 sm:h-8 bg-gray-400"></div>
                  <div className="absolute -top-px left-2/3 w-px h-6 sm:h-8 bg-gray-400"></div>
                </div>
              </div>

              <div className="flex justify-center space-x-2 sm:space-x-6 lg:space-x-12">
                {/* Director A */}
                <div className="flex flex-col items-center space-y-2 sm:space-y-3">
                  <div className="w-20 xs:w-24 sm:w-32 h-12 xs:h-14 sm:h-18 bg-linear-to-br from-green-300 to-green-500 border-2 border-green-600 rounded-lg flex flex-col items-center justify-center shadow-md">
                    <Shield className="w-3 h-3 sm:w-4 sm:h-4 text-green-900 mb-0.5 sm:mb-1" />
                    <div className="text-xs sm:text-sm font-bold text-green-900 whitespace-nowrap">
                      Director A
                    </div>
                    <div className="text-xs text-green-800 hidden xs:block whitespace-nowrap">
                      Operations
                    </div>
                  </div>
                  <div className="bg-gray-50 border border-gray-200 rounded p-1 sm:p-2 text-xs text-center w-20 xs:w-24 sm:max-w-28">
                    <div className="font-medium text-gray-700 hidden sm:block">Legacy:</div>
                    <div className="text-gray-600 text-xs">VP, PM</div>
                  </div>
                </div>

                {/* Director B */}
                <div className="flex flex-col items-center space-y-2 sm:space-y-3">
                  <div className="w-20 xs:w-24 sm:w-32 h-12 xs:h-14 sm:h-18 bg-linear-to-br from-purple-300 to-purple-500 border-2 border-purple-600 rounded-lg flex flex-col items-center justify-center shadow-md">
                    <DollarSign className="w-3 h-3 sm:w-4 sm:h-4 text-purple-900 mb-0.5 sm:mb-1" />
                    <div className="text-xs sm:text-sm font-bold text-purple-900 whitespace-nowrap">
                      Director B
                    </div>
                    <div className="text-xs text-purple-800 hidden xs:block whitespace-nowrap">
                      Resources
                    </div>
                  </div>
                  <div className="bg-gray-50 border border-gray-200 rounded p-1 sm:p-2 text-xs text-center w-20 xs:w-24 sm:max-w-28">
                    <div className="font-medium text-gray-700 hidden sm:block">Legacy:</div>
                    <div className="text-gray-600 text-xs">Treas, Sec</div>
                  </div>
                </div>

                {/* Director C */}
                <div className="flex flex-col items-center space-y-2 sm:space-y-3">
                  <div className="w-20 xs:w-24 sm:w-32 h-12 xs:h-14 sm:h-18 bg-linear-to-br from-blue-300 to-blue-500 border-2 border-blue-600 rounded-lg flex flex-col items-center justify-center shadow-md">
                    <MessageCircle className="w-3 h-3 sm:w-4 sm:h-4 text-blue-900 mb-0.5 sm:mb-1" />
                    <div className="text-xs sm:text-sm font-bold text-blue-900 whitespace-nowrap">
                      Director C
                    </div>
                    <div className="text-xs text-blue-800 hidden xs:block whitespace-nowrap">
                      Community
                    </div>
                  </div>
                  <div className="bg-gray-50 border border-gray-200 rounded p-1 sm:p-2 text-xs text-center w-20 xs:w-24 sm:max-w-28">
                    <div className="font-medium text-gray-700 hidden sm:block">Legacy:</div>
                    <div className="text-gray-600 text-xs">Chair, Mod</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Connection line to departments */}
            <div className="w-px h-8 bg-gray-400"></div>

            {/* Departments Section */}
            <div className="w-full">
              <div className="text-center mb-4">
                <div className="text-sm font-semibold text-gray-700 mb-2">
                  Community Departments
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
                  <div className="text-sm font-medium text-blue-800 mb-1">
                    ✓ Based on Landowners Association, Montana
                  </div>
                  <div className="text-xs text-blue-700">
                    These are the actual committees used by GLA - each becomes a department with its
                    own SubPanel structure for clear accountability and faster decision-making
                  </div>
                </div>
                <div className="text-xs text-gray-500">
                  Each department mirrors the same SubPanel structure
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {departments.map((dept) => {
                  const isExpanded = expandedDepartments.includes(dept.name);
                  const colorClasses = {
                    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-800',
                    blue: 'bg-blue-50 border-blue-200 text-blue-800',
                    purple: 'bg-purple-50 border-purple-200 text-purple-800',
                    red: 'bg-red-50 border-red-200 text-red-800',
                    green: 'bg-green-50 border-green-200 text-green-800',
                    indigo: 'bg-indigo-50 border-indigo-200 text-indigo-800',
                    slate: 'bg-slate-50 border-slate-200 text-slate-800',
                    orange: 'bg-orange-50 border-orange-200 text-orange-800',
                    teal: 'bg-teal-50 border-teal-200 text-teal-800',
                  };

                  return (
                    <div
                      key={dept.name}
                      className={`border rounded-lg p-4 ${colorClasses[dept.color as keyof typeof colorClasses]}`}
                    >
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleDepartment(dept.name)}
                        className="w-full justify-between p-2 h-auto hover:bg-linear-to-r hover:from-blue-500/90 hover:to-blue-600/90 hover:text-white rounded-md whitespace-normal"
                      >
                        <div className="flex items-start space-x-2 min-w-0 flex-1">
                          <dept.icon className="w-4 h-4 shrink-0 mt-0.5" />
                          <div className="text-left min-w-0 flex-1">
                            <div className="font-semibold text-sm wrap-break-word">{dept.name}</div>
                            <div className="text-xs opacity-75 wrap-break-word leading-tight">
                              {dept.description}
                            </div>
                          </div>
                        </div>
                        <div className="shrink-0 ml-2">
                          {isExpanded ? (
                            <ChevronDown className="w-4 h-4" />
                          ) : (
                            <ChevronRight className="w-4 h-4" />
                          )}
                        </div>
                      </Button>

                      {isExpanded && (
                        <div className="mt-4 space-y-3 border-t pt-3">
                          <div className="flex items-center justify-center gap-2 text-xs font-semibold">
                            <span>Department SubPanel</span>
                            <span className="text-gray-500">
                              ({dept.directorCount} Director{dept.directorCount > 1 ? 's' : ''})
                            </span>
                          </div>

                          {/* Department Elect */}
                          <div className="flex justify-center">
                            <div className="w-36 h-12 bg-linear-to-br from-yellow-200 to-yellow-400 border border-yellow-500 rounded-lg flex flex-col items-center justify-center">
                              <Crown className="w-3 h-3 text-yellow-800" />
                              <div className="text-xs font-bold text-yellow-800 whitespace-nowrap">
                                Department Elect
                              </div>
                            </div>
                          </div>

                          {/* Department Directors - Variable Count */}
                          <div className="flex justify-center space-x-2">
                            {Array.from({ length: dept.directorCount }, (_, i) => (
                              <div
                                key={i}
                                className={`w-28 h-10 border rounded flex items-center justify-center ${
                                  i === 0
                                    ? 'bg-linear-to-br from-green-200 to-green-400 border-green-500'
                                    : i === 1
                                      ? 'bg-linear-to-br from-purple-200 to-purple-400 border-purple-500'
                                      : 'bg-linear-to-br from-blue-200 to-blue-400 border-blue-500'
                                }`}
                              >
                                <div
                                  className={`text-xs font-bold whitespace-nowrap ${
                                    i === 0
                                      ? 'text-green-800'
                                      : i === 1
                                        ? 'text-purple-800'
                                        : 'text-blue-800'
                                  }`}
                                >
                                  Director {String.fromCharCode(65 + i)}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Key insight callout */}
          <div className="mt-8 space-y-4">
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 rounded-full bg-blue-500 mt-2 shrink-0"></div>
                <div>
                  <div className="text-sm font-semibold text-blue-800 mb-1">
                    Fractal Governance Structure
                  </div>
                  <div className="text-sm text-blue-700">
                    Every level uses the same SubPanel pattern: 1 Elect (override authority) + 1-3
                    Directors (decision-makers). At community level: 1 Community Elect + 1-3
                    Community Directors. At department level: 1 Department Elect + 1-3 Department
                    Directors. This creates a consistent, scalable structure from community-wide
                    decisions down to specific departments.
                  </div>
                </div>
              </div>
            </div>

            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 rounded-full bg-amber-500 mt-2 shrink-0"></div>
                <div>
                  <div className="text-sm font-semibold text-amber-800 mb-1">
                    Flexible Director Count
                  </div>
                  <div className="text-sm text-amber-700">
                    Each SubPanel requires only 1 Elect (override authority), but can have 1-3
                    Directors based on workload and complexity. Start small and add Directors as the
                    department grows.
                  </div>
                </div>
              </div>
            </div>

            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 rounded-full bg-green-500 mt-2 shrink-0"></div>
                <div>
                  <div className="text-sm font-semibold text-green-800 mb-1">
                    Landowners Association Committee Structure
                  </div>
                  <div className="text-sm text-green-700">
                    Each of these 9 committees from GLA becomes a dedicated department with its own
                    SubPanel: 1 Department Elect + 1-3 Department Directors based on workload. Road
                    maintenance handled by the Roads department, legal issues by the Legal
                    department, elections by the Election department. Clear chains of responsibility
                    eliminate the confusion and delays common in traditional Montana HOA boards.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
